import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card, Btn, Field, Input } from "@/components/ui-kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const SECTIONS = {
  organization: [
    ["company_name", "Legal Name"], ["display_name", "Display Name"], ["address", "Address"],
    ["city", "City"], ["state", "State"], ["state_code", "State Code"], ["pin", "PIN"],
    ["phone", "Phone"], ["email", "Email"], ["website", "Website"],
  ],
  statutory: [
    ["gstin", "GSTIN"], ["pan", "PAN"], ["epf_number", "EPF Establishment Number"],
    ["esic_code", "ESIC Employer Code"], ["labour_licence", "Labour Licence"], ["cin", "CIN"],
  ],
  bank: [
    ["account_name", "Account Name"], ["bank_name", "Bank Name"], ["account_number", "Account Number"],
    ["branch", "Branch"], ["ifsc", "IFSC"],
  ],
  invoice: [
    ["invoice_prefix", "Invoice Prefix"], ["next_invoice_number", "Next Invoice Number"],
    ["fy_numbering", "Financial Year Numbering (e.g. 2026-27)"], ["default_gst", "Default GST %"],
    ["invoice_footer", "Invoice Footer"], ["invoice_terms", "Terms / Notes"],
    ["signatory_name", "Authorised Signatory Name"], ["signatory_designation", "Signatory Designation"],
  ],
  payroll: [
    ["default_month_behaviour", "Default Payroll Month Behaviour"], ["default_rounding", "Default Rounding (nearest/up/down)"],
    ["lock_preference", "Payroll Lock Preference"], ["wage_settings", "Central/State Wage Notes"],
  ],
};

export default function Settings() {
  const { isSuper } = useAuth();
  const [form, setForm] = useState({});

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data || {})); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    try { await api.put("/settings", form); toast.success("Settings saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const uploadImg = (key) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, [key]: reader.result });
    reader.readAsDataURL(file);
  };

  const FieldGrid = ({ fields }) => (
    <div className="grid grid-cols-2 gap-4">
      {fields.map(([k, label]) => (
        <Field key={k} label={label}><Input value={form[k] || ""} onChange={set(k)} data-testid={`settings-${k}`} /></Field>
      ))}
    </div>
  );

  return (
    <div>
      <PageHeader title="Company Profile & Settings" subtitle="Reused automatically on invoices, payslips and reports"
        actions={isSuper && <Btn onClick={save} data-testid="save-settings-btn">Save Changes</Btn>} />
      <Card className="p-4">
        <Tabs defaultValue="organization">
          <TabsList className="flex-wrap">
            <TabsTrigger value="organization" data-testid="tab-organization">Organization</TabsTrigger>
            <TabsTrigger value="statutory" data-testid="tab-statutory">Statutory</TabsTrigger>
            <TabsTrigger value="bank" data-testid="tab-bank">Bank Details</TabsTrigger>
            <TabsTrigger value="invoice" data-testid="tab-invoice">Invoice</TabsTrigger>
            <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll</TabsTrigger>
            <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
          </TabsList>
          <TabsContent value="organization" className="mt-4"><FieldGrid fields={SECTIONS.organization} /></TabsContent>
          <TabsContent value="statutory" className="mt-4"><FieldGrid fields={SECTIONS.statutory} /></TabsContent>
          <TabsContent value="bank" className="mt-4"><FieldGrid fields={SECTIONS.bank} /></TabsContent>
          <TabsContent value="invoice" className="mt-4"><FieldGrid fields={SECTIONS.invoice} /></TabsContent>
          <TabsContent value="payroll" className="mt-4"><FieldGrid fields={SECTIONS.payroll} /></TabsContent>
          <TabsContent value="branding" className="mt-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="nk-overline block mb-2">Nakshatra Logo (used on documents)</label>
                <div className="border border-slate-200 rounded-md p-4 bg-slate-50 mb-2 flex items-center justify-center h-24">
                  <img src={form.logo || "/branding/nakshatra-logo.png"} alt="logo" style={{ maxHeight: 64, maxWidth: "100%", objectFit: "contain" }} />
                </div>
                <Input type="file" accept="image/*" onChange={uploadImg("logo")} data-testid="upload-logo" />
                <p className="text-xs text-slate-400 mt-1">Leave empty to use the supplied Nakshatra logo.</p>
              </div>
              <div>
                <label className="nk-overline block mb-2">Signature Image (optional)</label>
                <div className="border border-slate-200 rounded-md p-4 bg-slate-50 mb-2 flex items-center justify-center h-24">
                  {form.signature ? <img src={form.signature} alt="sign" style={{ maxHeight: 64, objectFit: "contain" }} /> : <span className="text-slate-400 text-sm">No signature</span>}
                </div>
                <Input type="file" accept="image/*" onChange={uploadImg("signature")} data-testid="upload-signature" />
                <p className="text-xs text-slate-400 mt-3">QrewHR developer logo appears subtly in the app footer.</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
